/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useRef} from 'react';
import {warn} from '@ion/core/src/logger/logger';
import {JobProgressUpdate} from '@ion/services/src/websocket/types';

const MAX_BUFFERED_CHANGES = 50;

export interface FetchWindowRequest<T> {
  read: () => T | undefined,
  load: () => Promise<T | undefined>,
  settle: (cached: T | undefined, response: T | undefined) => void
}

export interface FetchWindowBufferResult {
  loadInWindow: <T>(request: FetchWindowRequest<T>) => Promise<T | undefined>,
  openWindow: () => (() => void),
  record: (jobId: number, change: JobProgressUpdate) => void,
  changesFor: (jobId: number) => JobProgressUpdate[]
}

/**
 * Holds the `jobProgress` changes which arrive while a surface's own request is
 * in flight, so they can be replayed onto the response once it lands.
 *
 * A response overwrites the cache entry it was fetched into, so a change merged
 * inside that window is clobbered by it unless something kept a copy - and
 * reading the entry before awaiting cannot recover it, since the change had not
 * arrived at that point. That window is the likely case rather than the rare
 * one: `progress` ticks sub-second, so any running job emits inside one round
 * trip (design §7.4).
 *
 * This is not the ledger returning. What is held is raw changes rather than
 * merged jobs, its lifetime is one request, and it judges nothing: the replay
 * goes back through `mergeIntoJob`, so ordering stays the guard's alone.
 *
 * ```typescript
 *
 *   import useFetchWindowBuffer from './useFetchWindowBuffer';
 *
 *   const {loadInWindow, record, changesFor} = useFetchWindowBuffer();
 *
 *   // ... buffered on the way past, whatever the merge decides ...
 *   const onDelta = (change, jobId) => {
 *     record(jobId, change);
 *   };
 *
 *   // ... and replayed once the response has been reconciled ...
 *   const response = await loadInWindow({
 *     read   : () => queryClient.getQueryData(key),
 *     load   : () => loadItems(params),
 *     settle : (cached, incoming) => reconcile(cached, incoming, changesFor)
 *   });
 *
 * ```
 *
 * @returns `loadInWindow` to run a promise-shaped request inside a window,
 *          `openWindow` for a surface whose fetch is observed on its edges
 *          instead, `record` to buffer an arriving change, and `changesFor` to
 *          read a job's buffered changes back.
 */
export const useFetchWindowBuffer = (): FetchWindowBufferResult => {
  const buffered = useRef<Map<number, JobProgressUpdate[]>>(new Map());

  const windows = useRef(0);

  const openWindow = useCallback((): (() => void) => {
    let open = true;

    windows.current += 1;
    return () => {
      if (open) {
        open             = false;
        windows.current -= 1;
        if (!windows.current) {
          buffered.current.clear();
        }
      }
    };
  }, []);

  const record = useCallback((jobId: number, change: JobProgressUpdate): void => {
    if (windows.current) {
      const queue = buffered.current.get(jobId) ?? [];

      queue.push(change);
      if (queue.length > MAX_BUFFERED_CHANGES) {
        warn(`[ADM] jobProgress buffer for job ${jobId} is over ${MAX_BUFFERED_CHANGES} ` +
          'changes deep while a request is in flight, dropping the oldest');
        queue.shift();
      }
      buffered.current.set(jobId, queue);
    }
  }, []);

  const changesFor = useCallback((jobId: number): JobProgressUpdate[] => (
    buffered.current.get(jobId) ?? []
  ), []);

  const loadInWindow = useCallback(async<T>({
    read, load, settle
  }: FetchWindowRequest<T>): Promise<T | undefined> => {
    const cached = read();
    const close  = openWindow();

    try {
      const response = await load();

      settle(cached, response);
      return response;
    } finally {
      close();
    }
  }, [openWindow]);

  return {loadInWindow, openWindow, record, changesFor};
};

export default useFetchWindowBuffer;
